export type EvidenceSectionSource = 'assigned' | 'suggested' | 'additional' | 'all';

export const resolveEvidenceSectionSource = (section: {
  dataSource?: string;
  conditions?: Array<{ field?: string; $isNull?: number }>;
}): EvidenceSectionSource => {
  if (section.dataSource === 'assigned') return 'assigned';
  if (section.dataSource === 'suggested') return 'suggested';
  if (section.dataSource === 'additional') return 'additional';

  if (section.conditions) {
    const hasMatchCondition = section.conditions.some((condition) => condition.field === 'match');
    if (hasMatchCondition) {
      const hasAssignedMatch = section.conditions.some(
        (condition) => condition.field === 'match' && condition.$isNull === 1
      );
      const hasSuggestedMatch = section.conditions.some(
        (condition) => condition.field === 'match' && condition.$isNull === 0
      );
      if (hasAssignedMatch && !hasSuggestedMatch) return 'assigned';
      if (hasSuggestedMatch && !hasAssignedMatch) return 'suggested';
    }
  }

  return 'all';
};

export const isSuggestedSection = (section: {
  dataSource?: string;
  conditions?: Array<{ field?: string; $isNull?: number }>;
}): boolean => resolveEvidenceSectionSource(section) === 'suggested';

export const isAssignedSection = (section: {
  dataSource?: string;
  conditions?: Array<{ field?: string; $isNull?: number }>;
}): boolean => resolveEvidenceSectionSource(section) === 'assigned';

export const isPaginatedEvidenceSection = (section: {
  dataSource?: string;
  conditions?: Array<{ field?: string; $isNull?: number }>;
}): boolean => isSuggestedSection(section) || isAssignedSection(section);

export type TabSectionCountEntry = {
  label: string;
  dataSource: string;
  count: number;
};

export const resolveSectionCountFromTabTotals = (
  sectionCounts: TabSectionCountEntry[],
  section: { label?: string; dataSource?: string }
): number | undefined => {
  const sectionLabel = section.label || '';
  const dataSource = section.dataSource || '';

  const exactMatch = sectionCounts.find(
    (entry) => entry.label === sectionLabel && entry.dataSource === dataSource
  );
  if (exactMatch) {
    return exactMatch.count;
  }

  const dataSourceMatches = sectionCounts.filter((entry) => entry.dataSource === dataSource);
  if (dataSourceMatches.length === 1) {
    return dataSourceMatches[0].count;
  }

  return undefined;
};

export const isEvidenceSectionLoading = (
  section: { dataSource?: string; conditions?: Array<{ field?: string; $isNull?: number }> },
  loading: { assigned: boolean; suggested: boolean; additional: boolean }
): boolean => {
  const source = resolveEvidenceSectionSource(section);
  if (source === 'assigned') return loading.assigned;
  if (source === 'suggested') return loading.suggested;
  if (source === 'additional') return loading.additional;
  return loading.assigned || loading.suggested;
};

type TabSection = {
  dataSource?: string;
  conditions?: Array<{ field?: string; $isNull?: number }>;
};

type IdeaTab = {
  label?: string;
  name?: string;
  sections?: TabSection[];
};

export const getTabEvidenceSectionSources = (tab: IdeaTab | undefined): Set<EvidenceSectionSource> => {
  const sources = new Set<EvidenceSectionSource>();
  (tab?.sections || []).forEach((section) => {
    sources.add(resolveEvidenceSectionSource(section));
  });
  return sources;
};

type TabEvidenceCountLoadingOptions = {
  activeTab: string | null;
  tabs: IdeaTab[];
  activeFilters: Array<{ name?: string; selected?: unknown }>;
  trendFilters: Array<{ name?: string; selected?: unknown }>;
  evidencesLoading: boolean;
  matchesLoading: boolean;
  additionalEvidencesLoading: boolean;
  evidenceInclusion: 'total' | 'assigned' | string;
  suggestedTotalCount: number | null | undefined;
  isSuggestedCountLoadingByTabLabel: Map<string, boolean>;
  hasCachedCount: boolean;
  resolveSuggestedApiSectionFilters: (
    tabs: IdeaTab[],
    activeTab: string | null | undefined,
    businessFilters: Array<{ name?: string; selected?: unknown }>,
    trendFilters: Array<{ name?: string; selected?: unknown }>
  ) => {
    apiMergeEnabled: boolean;
    hasEmptyFilterIntersection: boolean;
  };
};

export const isTabEvidenceCountLoading = (
  tab: IdeaTab,
  options: TabEvidenceCountLoadingOptions
): boolean => {
  const tabLabel = tab.label || tab.name || '';
  if (!tabLabel || options.hasCachedCount) {
    return false;
  }

  const tabContext = options.resolveSuggestedApiSectionFilters(
    options.tabs,
    tabLabel,
    options.activeFilters,
    options.trendFilters
  );

  if (tabContext.hasEmptyFilterIntersection) {
    return false;
  }

  const sectionSources = getTabEvidenceSectionSources(tab);
  const needsAssigned = sectionSources.has('assigned') || sectionSources.has('all');
  const needsSuggested = sectionSources.has('suggested') || sectionSources.has('all');
  const needsAdditional = sectionSources.has('additional');
  const isTotalEvidenceInclusion = options.evidenceInclusion === 'total';
  const isActiveTab = tabLabel === options.activeTab;
  const hasSuggestedSection = (tab.sections || []).some(isSuggestedSection);
  const usesApiSuggestedCount = tabContext.apiMergeEnabled && hasSuggestedSection;

  if (needsAssigned && options.evidencesLoading) {
    return true;
  }

  if (needsSuggested && isTotalEvidenceInclusion) {
    if (usesApiSuggestedCount) {
      if (isActiveTab) {
        if (options.matchesLoading) {
          return true;
        }
      } else if (options.isSuggestedCountLoadingByTabLabel.get(tabLabel)) {
        return true;
      }
    } else if (isActiveTab && options.matchesLoading) {
      return true;
    }
  }

  if (needsAdditional && isTotalEvidenceInclusion && options.additionalEvidencesLoading) {
    return true;
  }

  return false;
};
